/* Dorah header: sticky compaction, mega-menu (hover / keyboard / touch), mobile drawer with focus trap. No dependencies. */
(() => {
  if (customElements.get('dorah-header')) return;
  const FOCUSABLE = 'a[href],button:not([disabled]),summary,input,select,textarea,[tabindex]:not([tabindex="-1"])';

  class DorahHeader extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-dh-item]')];
      this.tops = [...this.querySelectorAll('.dh__top')];
      this.drawer = this.querySelector('[data-dh-drawer]');
      this.burger = this.querySelector('[data-dh-drawer-open]');
      this.hoverMq = window.matchMedia('(hover: hover) and (min-width: 990px)');
      this.timer = null;
      this.initScroll();
      this.initMega();
      this.initDrawer();
      this.initEditor();
    }

    initScroll() {
      const sticky = this.dataset.sticky === 'true';
      let ticking = false;
      const update = () => {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 8);
        this.classList.toggle('is-compact', sticky && y > 40);
        ticking = false;
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
      const bar = this.querySelector('.dh__bar');
      bar.addEventListener('mouseenter', () => this.hoverMq.matches && this.classList.add('is-hover'));
      bar.addEventListener('mouseleave', () => this.classList.remove('is-hover'));
    }

    open(item, focusFirst) {
      clearTimeout(this.timer);
      this.items.forEach((i) => i !== item && this.setOpen(i, false));
      this.setOpen(item, true);
      if (focusFirst) {
        const first = item.querySelector('[data-dh-panel] ' + FOCUSABLE);
        if (first) first.focus();
      }
    }

    close(item, returnFocus) {
      this.setOpen(item, false);
      if (returnFocus) item.querySelector('[data-dh-top]').focus();
    }

    closeAll() { this.items.forEach((i) => this.setOpen(i, false)); }

    setOpen(item, state) {
      item.classList.toggle('is-open', state);
      item.querySelector('[data-dh-top]').setAttribute('aria-expanded', state ? 'true' : 'false');
      this.classList.toggle('is-menu', this.items.some((i) => i.classList.contains('is-open')));
    }

    initMega() {
      this.items.forEach((item) => {
        const top = item.querySelector('[data-dh-top]');
        item.addEventListener('mouseenter', () => {
          if (!this.hoverMq.matches) return;
          clearTimeout(this.timer);
          const anyOpen = this.items.some((i) => i.classList.contains('is-open'));
          this.timer = setTimeout(() => this.open(item), anyOpen ? 0 : 70);
        });
        item.addEventListener('mouseleave', () => {
          if (!this.hoverMq.matches) return;
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.close(item), 140);
        });
        top.addEventListener('click', (e) => {
          // touch / no-hover: first tap opens, second tap follows the link
          if (!this.hoverMq.matches && !item.classList.contains('is-open')) { e.preventDefault(); this.open(item); }
        });
        top.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); this.open(item, true); }
        });
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && item.classList.contains('is-open')) { e.preventDefault(); this.close(item, true); }
        });
        item.addEventListener('focusout', (e) => {
          if (e.relatedTarget && !item.contains(e.relatedTarget)) this.close(item);
        });
      });
      this.tops.forEach((top, idx) => top.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const next = this.tops[(idx + (e.key === 'ArrowRight' ? 1 : -1) + this.tops.length) % this.tops.length];
        this.closeAll();
        next.focus();
      }));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeAll(); });
      this.hoverMq.addEventListener('change', () => this.closeAll());
    }

    initDrawer() {
      if (!this.drawer || !this.burger) return;
      this.burger.addEventListener('click', () => this.openDrawer());
      this.drawer.querySelectorAll('[data-dh-drawer-close]').forEach((b) => b.addEventListener('click', () => this.closeDrawer()));
      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); this.closeDrawer(); return; }
        if (e.key !== 'Tab') return;
        const f = [...this.drawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      // one accordion open at a time
      this.drawer.querySelectorAll('details.dh__acc').forEach((d) => d.addEventListener('toggle', () => {
        if (d.open) this.drawer.querySelectorAll('details.dh__acc[open]').forEach((o) => o !== d && (o.open = false));
      }));
    }

    openDrawer() {
      this.drawer.hidden = false;
      document.documentElement.classList.add('dh-lock');
      this.burger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        const btn = this.drawer.querySelector('.dh__panel-head [data-dh-drawer-close]');
        if (btn) btn.focus({ preventScroll: true });
      }));
    }

    closeDrawer() {
      if (this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('dh-lock');
      this.burger.setAttribute('aria-expanded', 'false');
      const panel = this.drawer.querySelector('.dh__panel');
      const done = () => { this.drawer.hidden = true; panel.removeEventListener('transitionend', done); };
      panel.addEventListener('transitionend', done);
      setTimeout(done, 400);
      this.burger.focus({ preventScroll: true });
    }

    initEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      document.addEventListener('shopify:block:select', (e) => {
        const item = this.querySelector('[data-dh-block="' + e.detail.blockId + '"]');
        if (item) this.open(item);
      });
      document.addEventListener('shopify:section:deselect', () => this.closeAll());
    }
  }

  customElements.define('dorah-header', DorahHeader);
})();
